/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowDown, ArrowRightLeft, Building2, History, Link2, Network, Search, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type { TldSupport } from '../../api/areas/registry';
import type {
  ChannelBatchBody,
  ChannelBatchResult,
  ConnectionSummary,
  IntegrationEnvironment,
  ProposeChannelBody,
  TldChannel,
  TldChannelRow,
  TldChannelState,
  TldChannelVersion,
  TldClass,
} from '../../api/areas/integrations';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, StatCard, StatGrid, type Column } from '../../components/blocks';
import { TwoColumn } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { formatCount } from '../../lib/format';
import { toASCII } from '../../lib/punycode';
import { useApi } from '../../lib/useApi';
import { TldSetupButton } from '../registry/TldSetupWizard';
import { PendingChangesPanel, TLD_CHANGE_KINDS } from './changes';
import {
  ChangeDialog,
  ChannelBadge,
  ChannelGuide,
  channelOf,
  ConfirmCheck,
  ConnectionLink,
  DeploymentNote,
  EnvironmentBanner,
  IntegrationError,
  Panel,
  ReasonField,
  SelectField,
  StaffName,
  StatusBadge,
  tx,
  useIntegrationContext,
  WarningList,
  When,
  WithIntegrationContext,
  WriteOutcome,
} from './shared';

const ALL = '__all__';
const TLD_CLASSES: readonly TldClass[] = ['VN', 'GTLD', 'NEW_GTLD', 'CCTLD', 'OTHER'];

/** `/settings/tlds` — which connection handles new registrations and transfers-in of each TLD
 * (one channel per TLD: this server's, ADR-018). */
export function TldChannelsPage() {
  const { t } = useTranslation('integrations');
  useDocumentTitle(t('tlds.title'));
  const [tick, setTick] = useState(0);
  return (
    <>
      <PageHeader title={t('tlds.title')} subtitle={t('tlds.subtitle')} actions={<TldSetupButton onChanged={() => setTick((n) => n + 1)} />} />
      <WithIntegrationContext>
        <TldChannelsView key={tick} />
      </WithIntegrationContext>
    </>
  );
}

/** The connection new registrations and transfers of the TLD go through. */
function ConnectionCell({ state }: { state: TldChannelState | undefined }) {
  const { t } = useTranslation('integrations');
  if (!state) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="grid min-w-44 gap-1">
      <ConnectionLink connection={state.connection} />
      {state.connection.adapter_status !== 'AVAILABLE' ? <span className="text-xs text-warning">{t('tlds.noAdapter')}</span> : null}
      {!state.connection_ready ? <span className="text-xs text-warning">{t('tlds.notReady')}</span> : null}
      {state.pending ? (
        <span className="flex flex-wrap items-center gap-1 text-xs">
          <Badge tone="pending">{t('tlds.pending')}</Badge>
          <span className="text-muted-foreground">→ {state.pending.connection.name}</span>
        </span>
      ) : null}
    </div>
  );
}

/** Whether the connection is the TLD's own choice or its default, in words. */
function SourceCell({ row }: { row: TldChannelRow }) {
  const { t } = useTranslation('integrations');
  if (!row.current) return <span className="text-muted-foreground">—</span>;
  return row.current.source === 'CONFIGURED' ? (
    <span className="text-sm">{t('tlds.own')}</span>
  ) : (
    <span className="text-sm text-muted-foreground">{t('tlds.inherited', { name: row.default_connection.name })}</span>
  );
}

/** "12 tên miền qua VNNIC, 3 qua WebNIC". */
function DomainsCell({ row }: { row: TldChannelRow }) {
  const { t } = useTranslation('integrations');
  if (!row.domains_by_connection.length) return <span className="text-xs text-muted-foreground">{t('tlds.noDomains')}</span>;
  return (
    <span className="text-sm">
      {row.domains_by_connection
        .map((d, i) => (i === 0 ? t('tlds.domainsVia', { count: d.domains, formatted: formatCount(d.domains), name: d.connection.name }) : t('tlds.domainsViaMore', { formatted: formatCount(d.domains), name: d.connection.name })))
        .join(', ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// "Registrar & định tuyến" (reference screen 17): who registers through each connection, the
// counts, and where a new name would go.

const tldCount = (c: ConnectionSummary) => c.tld_counts.configured + c.tld_counts.by_default;

function RoutingTiles({ connections, rows }: { connections: readonly ConnectionSummary[]; rows: readonly TldChannelRow[] }) {
  const { t } = useTranslation('integrations');
  const usable = connections.filter((c) => c.status !== 'RETIRED');
  const own = usable.reduce((n, c) => n + c.tld_counts.configured, 0);
  const inherited = usable.reduce((n, c) => n + c.tld_counts.by_default, 0);
  const onNotReady = usable.filter((c) => !c.settings?.ready).reduce((n, c) => n + tldCount(c), 0);
  const waiting = rows.filter((r) => r.current.pending).length;
  return (
    <StatGrid>
      <StatCard
        label={t('routing.tiles.connections')}
        value={formatCount(usable.length)}
        icon={Building2}
        hint={t('routing.tiles.connectionsHint', { direct: usable.filter((c) => c.kind === 'REGISTRY').length, upstream: usable.filter((c) => c.kind === 'UPSTREAM_REGISTRAR').length })}
      />
      <StatCard label={t('routing.tiles.tlds')} value={formatCount(own + inherited)} icon={Network} hint={t('routing.tiles.tldsHint', { own, inherited })} />
      <StatCard label={t('routing.tiles.inUse')} value={formatCount(usable.filter((c) => tldCount(c) > 0).length)} icon={Link2} hint={t('routing.tiles.inUseHint', { total: usable.length })} />
      <StatCard
        label={t('routing.tiles.review')}
        value={formatCount(onNotReady + waiting)}
        icon={TriangleAlert}
        tone={onNotReady + waiting ? 'warn' : 'neutral'}
        hint={t('routing.tiles.reviewHint', { tlds: onNotReady, changes: waiting })}
      />
    </StatGrid>
  );
}

/** Every usable connection with who is the registrar of the names it registers. */
function RegistrarsPanel({ connections }: { connections: readonly ConnectionSummary[] }) {
  const { t } = useTranslation('integrations');
  const columns: Column<ConnectionSummary>[] = [
    {
      key: 'c',
      header: t('routing.registrars.col.connection'),
      className: 'min-w-44 whitespace-normal',
      cell: (c) => (
        <span className="grid justify-items-start gap-1">
          <ConnectionLink connection={c} />
          <ChannelBadge channel={channelOf(c.kind, c.protocol)} short />
        </span>
      ),
    },
    {
      key: 'role',
      header: t('routing.registrars.col.role'),
      className: 'min-w-40 whitespace-normal',
      cell: (c) => (
        <span className="grid justify-items-start gap-1">
          <Badge tone={c.role === 'UPSTREAM_REGISTRAR' ? 'pending' : 'info'} icon={false} className="whitespace-normal">
            {t(`role.value.${c.role ?? 'REGISTRAR_OF_RECORD'}`)}
          </Badge>
          {c.upstream ? <span className="text-xs text-muted-foreground">{t('routing.registrars.iana', { id: c.upstream.iana_id, name: c.upstream.trade_name })}</span> : null}
        </span>
      ),
    },
    { key: 'tlds', header: t('routing.registrars.col.tlds'), numeric: true, cell: (c) => (tldCount(c) ? formatCount(tldCount(c)) : <span className="text-muted-foreground">{t('routing.registrars.noTlds')}</span>) },
    {
      key: 'state',
      header: t('routing.registrars.col.status'),
      cell: (c) =>
        c.status !== 'ACTIVE' ? (
          <StatusBadge group="connectionStatus" value={c.status} />
        ) : (
          <Badge tone={c.settings?.ready ? 'ok' : 'warn'}>{c.settings?.ready ? t('routing.registrars.ready') : t('routing.registrars.notReady')}</Badge>
        ),
    },
  ];
  return (
    <Panel title={t('routing.registrars.title')} description={t('routing.registrars.description')} flush>
      <DataTable columns={columns} rows={connections.filter((c) => c.status !== 'RETIRED')} rowKey={(c) => c.id} empty={<EmptyState title={t('connections.empty')} />} />
    </Panel>
  );
}

function RouteNode({ label, children, active }: { label: string; children: ReactNode; active?: boolean }) {
  return (
    <div className={cn('grid gap-0.5 rounded-lg border px-3 py-2.5 text-center text-sm', active && 'border-primary/40 bg-accent text-accent-foreground')}>
      <span className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">{label}</span>
      {children}
    </div>
  );
}

/** "Which route a name takes": the TLD it falls in, that TLD's connection now, the registry. Reads only. */
function RouteExplainer() {
  const { t } = useTranslation('integrations');
  const [name, setName] = useState('');
  const [asked, setAsked] = useState('');
  const result = useApi(
    async (signal) => {
      if (!asked) return null;
      let ascii = '';
      try {
        ascii = toASCII(asked);
      } catch {
        // not a name: said below
      }
      if (!ascii.includes('.')) return { invalid: true as const };
      const list = await api.listTldChannels({ limit: 500 }, { signal });
      // The longest zone the name ends with (com.vn before vn).
      const row = list.items.filter((r) => ascii.endsWith(`.${r.zone}`) && ascii.length > r.zone.length + 1).sort((a, b) => b.zone.length - a.zone.length)[0];
      if (!row) return { row: null };
      const support = await api.getTldConnections([row.tld_id], { signal }).catch(() => null);
      return { row, operator: support?.tlds[0]?.operator ?? null };
    },
    [asked],
  );
  const submit = () => setAsked(name.trim().toLowerCase().replace(/\.$/, ''));
  const out = result.status === 'success' ? result.data : null;
  return (
    <Panel title={t('routing.explain.title')} description={t('routing.explain.description')}>
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label={t('routing.explain.name')} className="min-w-0 flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('routing.explain.placeholder')} className="font-mono" autoComplete="off" spellCheck={false} />
        </Field>
        <Button type="submit" variant="outline" disabled={!name.trim()}>
          <Search /> {t('routing.explain.run')}
        </Button>
      </form>
      <div className="mt-4 space-y-2">
        {out && 'invalid' in out ? <p className="text-sm text-warning">{t('routing.explain.invalid')}</p> : null}
        {result.status === 'loading' && asked ? <Loading rows={2} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {out && 'row' in out && !out.row ? <p className="text-sm text-muted-foreground">{t('routing.explain.noTld', { name: asked })}</p> : null}
        {out && 'row' in out && out.row ? (
          <>
            <RouteNode label={t('routing.explain.tld')}>
              <span className="font-mono font-semibold">.{out.row.unicode_zone || out.row.zone}</span>
              <span className="text-xs text-muted-foreground">{t(`tldClass.${out.row.tld_class}`)}</span>
            </RouteNode>
            <ArrowDown className="mx-auto size-4 text-muted-foreground" aria-hidden="true" />
            <RouteNode label={t('routing.explain.connection')} active>
              <span className="font-semibold">{out.row.current.connection.name}</span>
              <span className="flex flex-wrap items-center justify-center gap-1.5 text-xs">
                <ChannelBadge channel={out.row.current.channel} short />
                <span className="text-muted-foreground">{out.row.current.source === 'CONFIGURED' ? t('routing.explain.own') : t('routing.explain.inherited')}</span>
              </span>
            </RouteNode>
            <ArrowDown className="mx-auto size-4 text-muted-foreground" aria-hidden="true" />
            <RouteNode label={t('routing.explain.registry')}>
              <span>{out.operator ?? t('routing.explain.registryUnknown')}</span>
            </RouteNode>
            {out.row.status !== 'ACTIVE' ? <Callout tone="warn">{t('routing.explain.suspended')}</Callout> : null}
            {!out.row.current.connection_ready ? <Callout tone="warn">{t('routing.explain.notReady')}</Callout> : null}
            {out.row.current.pending ? <Callout tone="pending">{t('routing.explain.pending', { name: out.row.current.pending.connection.name })}</Callout> : null}
            <Link to={`/registry/tlds/${encodeURIComponent(out.row.tld_id)}`} className="inline-block text-sm font-medium text-primary hover:underline">
              {t('routing.explain.openTld', { zone: out.row.unicode_zone || out.row.zone })}
            </Link>
          </>
        ) : null}
      </div>
    </Panel>
  );
}

function RoutingOverview({ connections, rows }: { connections: readonly ConnectionSummary[] | undefined; rows: readonly TldChannelRow[] }) {
  const { t } = useTranslation('integrations');
  if (!connections) return <Loading rows={2} />;
  return (
    <div className="space-y-5">
      <RoutingTiles connections={connections} rows={rows} />
      <TwoColumn
        main={<RegistrarsPanel connections={connections} />}
        side={
          <>
            <RouteExplainer />
            <Callout tone="warn" title={t('routing.noFailover.title')}>
              {t('routing.noFailover.body')}
            </Callout>
          </>
        }
      />
    </div>
  );
}

function TldChannelsView() {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const [cursors, setCursors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [changing, setChanging] = useState<TldChannelRow | null>(null);
  const [historyOf, setHistoryOf] = useState<TldChannelRow | null>(null);
  const [batch, setBatch] = useState(false);
  const channel = (params.get('channel') ?? '') as TldChannel | '';
  const connectionId = params.get('connection') ?? '';
  const source = (params.get('source') ?? '') as 'CONFIGURED' | 'DEFAULT' | '';
  const tldClass = (params.get('class') ?? '') as TldClass | '';
  const q = params.get('q') ?? '';
  // The TLD page fixes `tld_id`: this one TLD, without list filters or batch selection.
  const oneTld = params.get('tld_id') ?? '';
  const [search, setSearch] = useState(q);
  const cursor = cursors[cursors.length - 1];
  const connections = useApi((signal) => api.listConnections({}, { signal }), [tick]);
  const result = useApi(
    (signal) =>
      api.listTldChannels(
        {
          channel: channel || undefined,
          connection_id: connectionId || undefined,
          source: source || undefined,
          tld_class: tldClass || undefined,
          q: q || undefined,
          tld_id: oneTld || undefined,
          after: cursor,
          limit: 100,
        },
        { signal },
      ),
    [channel, connectionId, source, tldClass, q, oneTld, cursor, tick],
  );
  const data = useRetained(result.data);
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setCursors([]);
    setSelected(new Set());
    setParams(next, { replace: true });
  };
  const reload = () => setTick((n) => n + 1);
  const rows = data?.items ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.tld_id));
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allColumns: Column<TldChannelRow>[] = [
    {
      key: 'sel',
      header: <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v === true ? new Set(rows.map((r) => r.tld_id)) : new Set())} aria-label={t('tlds.selectAll')} />,
      cell: (r) => <Checkbox checked={selected.has(r.tld_id)} onCheckedChange={() => toggle(r.tld_id)} aria-label={t('tlds.select', { zone: r.unicode_zone })} />,
      className: 'w-10',
    },
    {
      key: 'zone',
      header: t('tlds.col.tld'),
      cell: (r) => (
        <div className="grid gap-1">
          <Link to={`/registry/tlds/${encodeURIComponent(r.tld_id)}`} className="font-mono text-base font-bold text-primary hover:underline" title={t('tlds.openTld', { zone: r.unicode_zone || r.zone })}>
            .{r.unicode_zone || r.zone}
          </Link>
          {r.unicode_zone && r.unicode_zone !== r.zone ? <span className="font-mono text-xs text-muted-foreground">.{r.zone}</span> : null}
          <span className="flex flex-wrap gap-1">
            <Badge tone="neutral" icon={false}>
              {t(`tldClass.${r.tld_class}`)}
            </Badge>
            {r.status !== 'ACTIVE' ? <StatusBadge group="connectionStatus" value={r.status} /> : null}
          </span>
        </div>
      ),
    },
    {
      key: 'kind',
      header: t('tlds.col.kind'),
      cell: (r) => (r.current ? <ChannelBadge channel={r.current.channel} short /> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'channel',
      header: t('tlds.col.connection'),
      className: 'whitespace-normal',
      cell: (r) => <ConnectionCell state={r.current} />,
    },
    {
      key: 'source',
      header: t('tlds.col.source'),
      className: 'min-w-36 whitespace-normal',
      cell: (r) => <SourceCell row={r} />,
    },
    {
      key: 'domains',
      header: t('tlds.col.domains'),
      className: 'min-w-40 whitespace-normal',
      cell: (r) => <DomainsCell row={r} />,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('tlds.col.actions')}</span>,
      cell: (r) => (
        <div className="flex flex-wrap justify-end gap-1">
          <Button size="xs" variant="outline" onClick={() => setChanging(r)}>
            <ArrowRightLeft /> {t('tlds.change')}
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setHistoryOf(r)}>
            <History /> {t('tlds.history')}
          </Button>
        </div>
      ),
    },
  ];

  // On one TLD's page the zone and batch selection repeat the page itself.
  const columns = oneTld ? allColumns.filter((c) => c.key !== 'sel' && c.key !== 'zone') : allColumns;
  const connectionOptions = (connections.data?.items ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }));

  return (
    <div className="space-y-6">
      <DeploymentNote />
      {oneTld ? null : <RoutingOverview connections={connections.data?.items} rows={rows} />}
      <details className="rounded-lg border px-4 py-3 text-sm">
        <summary className="cursor-pointer font-medium">{t('tlds.guideTitle')}</summary>
        <div className="mt-3 space-y-3">
          <p>{t('tlds.explain')}</p>
          <ChannelGuide />
        </div>
      </details>
      <PendingChangesPanel kinds={TLD_CHANGE_KINDS} tldId={oneTld || undefined} refreshKey={tick} onChanged={reload} />
      <Panel flush title={oneTld ? undefined : t('routing.matrix.title')} description={oneTld ? undefined : t('routing.matrix.description')}>
        <div className={oneTld ? 'hidden' : 'px-5'}>
          <FilterBar
            actions={
              selected.size ? (
                <Button size="sm" onClick={() => setBatch(true)}>
                  <ArrowRightLeft /> {t('tlds.batch', { count: selected.size })}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('tlds.filter.channel')}
              value={channel || ALL}
              onChange={(v) => set('channel', v === ALL ? '' : v)}
              options={[{ value: ALL, label: t('common.all') }, ...(['DIRECT_EPP', 'REGISTRY_REST', 'UPSTREAM_REGISTRAR'] as const).map((c) => ({ value: c, label: t(`channel.${c}`) }))]}
              className="min-w-56"
            />
            <SelectField
              label={t('tlds.filter.connection')}
              value={connectionId || ALL}
              onChange={(v) => set('connection', v === ALL ? '' : v)}
              options={[{ value: ALL, label: t('common.all') }, ...connectionOptions]}
              className="min-w-56"
            />
            <SelectField
              label={t('tlds.filter.source')}
              value={source || ALL}
              onChange={(v) => set('source', v === ALL ? '' : v)}
              options={[{ value: ALL, label: t('common.all') }, { value: 'CONFIGURED', label: t('source.CONFIGURED') }, { value: 'DEFAULT', label: t('source.DEFAULT') }]}
              className="min-w-40"
            />
            <SelectField
              label={t('tlds.filter.class')}
              value={tldClass || ALL}
              onChange={(v) => set('class', v === ALL ? '' : v)}
              options={[{ value: ALL, label: t('common.all') }, ...TLD_CLASSES.map((c) => ({ value: c, label: t(`tldClass.${c}`) }))]}
              className="min-w-40"
            />
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                set('q', search.trim());
              }}
            >
              <Field label={t('tlds.filter.search')}>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('tlds.filter.searchPlaceholder')} className="w-40" />
              </Field>
              <Button type="submit" variant="outline" size="icon" aria-label={t('tlds.filter.search')}>
                <Search />
              </Button>
            </form>
          </FilterBar>
        </div>
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('tlds.loadFailed')} />
          </div>
        ) : !data ? (
          <div className="px-5">
            <Loading label={t('tlds.loading')} rows={5} />
          </div>
        ) : (
          <>
            <DataTable columns={columns} rows={rows} rowKey={(r) => r.tld_id} empty={<EmptyState title={t('tlds.empty')} />} />
            {data.next_after || cursors.length ? (
              <div className="flex justify-end gap-2 border-t px-4 py-3">
                <Button size="sm" variant="outline" disabled={!cursors.length} onClick={() => setCursors((c) => c.slice(0, -1))}>
                  {t('common.newer')}
                </Button>
                <Button size="sm" variant="outline" disabled={!data.next_after} onClick={() => data.next_after && setCursors((c) => [...c, data.next_after as string])}>
                  {t('common.more')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Panel>

      <ChangeDialog open={!!changing} onOpenChange={(o) => !o && setChanging(null)} title={changing ? t('channelDialog.title', { zone: changing.unicode_zone }) : ''} description={t('channelDialog.description')} wide>
        {changing ? (
          <ChannelForm row={changing} env={ctx.integration_environment} connections={connections.data?.items ?? []} onClose={() => setChanging(null)} onDone={reload} />
        ) : null}
      </ChangeDialog>
      <ChangeDialog open={batch} onOpenChange={setBatch} title={t('batchDialog.title', { count: selected.size })} description={t('batchDialog.description')} wide>
        <BatchForm
          rows={rows.filter((r) => selected.has(r.tld_id))}
          env={ctx.integration_environment}
          connections={connections.data?.items ?? []}
          onClose={() => setBatch(false)}
          onDone={() => {
            setSelected(new Set());
            reload();
          }}
        />
      </ChangeDialog>
      {oneTld ? (
        <Panel title={rows[0] ? t('tldHistory.title', { zone: rows[0].unicode_zone || rows[0].zone }) : t('tldHistory.inline')}>
          <TldHistory tldId={oneTld} zone={rows[0]?.unicode_zone || rows[0]?.zone} />
        </Panel>
      ) : null}
      <ChangeDialog open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)} title={historyOf ? t('tldHistory.title', { zone: historyOf.unicode_zone }) : ''} description={t('tldHistory.description')} wide>
        {historyOf ? <TldHistory tldId={historyOf.tld_id} zone={historyOf.unicode_zone || historyOf.zone} /> : null}
      </ChangeDialog>
    </div>
  );
}

function ConnectionOption({ c }: { c: ConnectionSummary }) {
  return (
    <span className="flex items-center gap-2">
      {c.name} <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
    </span>
  );
}

type TFn = (key: string, o?: Record<string, unknown>) => string;

/** How one connection serves the zone(s) of a dialog, in words. */
interface Verdict {
  supports: TldSupport;
  reason: string;
}

/**
 * Which connections take the dialog's TLDs (`GET /registry/tld-connections`): per connection, YES
 * / UNKNOWN / NO with the reason; for several TLDs, NO when it does not take one of them.
 * `'loading'` while asking; null when it could not be asked (every connection is then offered).
 */
function useVerdicts(rows: readonly Pick<TldChannelRow, 'tld_id' | 'zone' | 'unicode_zone'>[]): Map<string, Verdict> | null | 'loading' {
  const { t } = useTranslation('integrations');
  const tr = t as unknown as TFn;
  const ids = rows.map((r) => r.tld_id);
  const result = useApi((signal) => api.getTldConnections(ids, { signal }), [ids.join(',')]);
  if (result.status === 'loading') return 'loading';
  if (result.status === 'error') return null;
  const zoneOf = (id: string) => {
    const r = rows.find((x) => x.tld_id === id);
    return r ? r.unicode_zone || r.zone : '';
  };
  const verdicts = new Map<string, Verdict>();
  for (const c of result.data.connections) {
    const per = result.data.tlds.map((z) => ({ zone: zoneOf(z.tld_id) || z.zone, v: z.connections.find((x) => x.connection_id === c.id) }));
    const only = per.length === 1 ? per[0] : undefined;
    if (only) {
      verdicts.set(
        c.id,
        only.v
          ? { supports: only.v.supports, reason: tr(`support.basis.${only.v.basis}`, { zone: only.zone, name: c.name, operators: (only.v.operators ?? []).join(', ') }) }
          : { supports: 'UNKNOWN', reason: '' },
      );
      continue;
    }
    const no = per.filter((p) => p.v?.supports === 'NO').map((p) => `.${p.zone}`);
    const unknown = per.filter((p) => !p.v || p.v.supports === 'UNKNOWN').map((p) => `.${p.zone}`);
    verdicts.set(
      c.id,
      no.length
        ? { supports: 'NO', reason: t('support.batchMissing', { list: no.join(', ') }) }
        : unknown.length
          ? { supports: 'UNKNOWN', reason: t('support.batchUnknown', { list: unknown.join(', ') }) }
          : { supports: 'YES', reason: t('support.groupBatch.YES') },
    );
  }
  return verdicts;
}

const SUPPORT_ORDER: TldSupport[] = ['YES', 'UNKNOWN', 'NO'];

/**
 * The connections the TLD(s) can move to: those that take it first, those not known next (they
 * can be picked, with a hint), those that do not take it folded away and disabled, each with
 * its reason. Without the answer (error) every usable connection is offered, grouped by channel.
 */
function ConnectionPicker({
  value,
  onChange,
  connections,
  current,
  verdicts,
  zone,
}: {
  value: string;
  onChange: (id: string) => void;
  connections: readonly ConnectionSummary[];
  current?: string;
  verdicts: Map<string, Verdict> | null | 'loading';
  /** The TLD of a single-TLD dialog; undefined for several. */
  zone?: string;
}) {
  const { t } = useTranslation('integrations');
  const [showNo, setShowNo] = useState(false);
  const usable = connections.filter((c) => c.status !== 'RETIRED');
  if (verdicts === 'loading') return <Loading label={t('support.loading')} rows={2} />;
  const option = (c: ConnectionSummary, verdict: Verdict | undefined) => {
    const refused = verdict?.supports === 'NO';
    const disabled = c.id === current || refused;
    return (
      <label
        key={c.id}
        className={cn(
          'flex items-start gap-2 rounded-md border p-2 text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          verdict?.supports === 'UNKNOWN' && 'border-dashed',
          value === c.id && 'border-primary bg-accent/40',
        )}
      >
        <input type="radio" name="connection" className="mt-1" checked={value === c.id} disabled={disabled} onChange={() => onChange(c.id)} />
        <span className="grid min-w-0 gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <ConnectionOption c={c} />
            <ChannelBadge channel={channelOf(c.kind, c.protocol)} short />
          </span>
          {verdict?.reason ? <span className={cn('text-xs', verdict.supports === 'YES' ? 'text-success' : 'text-muted-foreground')}>{verdict.reason}</span> : null}
          {c.id === current ? <span className="text-xs text-muted-foreground">{t('channelDialog.current')}</span> : null}
          {c.adapter_status !== 'AVAILABLE' ? <span className="text-xs text-warning">{t('adapterStatus.NOT_AVAILABLE_NEEDS_SPECIFICATION')}</span> : null}
          {c.status === 'SUSPENDED' ? <span className="text-xs text-warning">{t('connectionStatus.SUSPENDED')}</span> : null}
        </span>
      </label>
    );
  };
  if (!verdicts) {
    const groups: TldChannel[] = ['DIRECT_EPP', 'REGISTRY_REST', 'UPSTREAM_REGISTRAR'];
    return (
      <fieldset className="grid gap-3">
        <legend className="mb-1 text-sm font-medium">{t('channelDialog.newConnection')}</legend>
        <Callout tone="warn">{t('support.failed')}</Callout>
        {groups.map((g) => {
          const items = usable.filter((c) => channelOf(c.kind, c.protocol) === g);
          if (!items.length) return null;
          return (
            <div key={g} className="grid gap-1.5">
              <p className="text-xs text-muted-foreground">{t(`channelHint.${g}`)}</p>
              {items.map((c) => option(c, undefined))}
            </div>
          );
        })}
      </fieldset>
    );
  }
  return (
    <fieldset className="grid gap-4">
      <legend className="mb-1 text-sm font-medium">{t('channelDialog.newConnection')}</legend>
      {SUPPORT_ORDER.map((s) => {
        const items = usable.filter((c) => (verdicts.get(c.id)?.supports ?? 'UNKNOWN') === s);
        if (!items.length) return null;
        const heading = zone ? t(`support.group.${s}`, { zone }) : t(`support.groupBatch.${s}`);
        if (s === 'NO' && !showNo) {
          return (
            <button key={s} type="button" className="w-fit text-left text-xs font-medium text-primary hover:underline" onClick={() => setShowNo(true)}>
              {t('support.showHidden', { count: items.length })}
            </button>
          );
        }
        return (
          <div key={s} className="grid gap-1.5">
            <p className="text-xs font-semibold tracking-[0.06em] text-muted-foreground uppercase">{heading}</p>
            {s === 'UNKNOWN' ? <p className="text-xs text-muted-foreground">{t('support.unknownHint')}</p> : null}
            {items.map((c) => option(c, verdicts.get(c.id)))}
            {s === 'NO' ? (
              <button type="button" className="w-fit text-left text-xs font-medium text-primary hover:underline" onClick={() => setShowNo(false)}>
                {t('support.hideHidden')}
              </button>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

function PreChecks({ target, gtld, domainsElsewhere, currentName }: { target: ConnectionSummary | undefined; gtld: boolean; domainsElsewhere: number; currentName: string }) {
  const { t } = useTranslation('integrations');
  if (!target) return null;
  const settings = target.settings;
  const notes: string[] = [];
  if (target.adapter_status !== 'AVAILABLE') notes.push(t('warnings.ADAPTER_NOT_AVAILABLE'));
  if (!settings?.enabled || !settings.ready) notes.push(t('warnings.CONNECTION_NOT_CONFIGURED'));
  if (gtld && target.kind === 'REGISTRY') notes.push(t('channelDialog.sponsorCheck'));
  return (
    <div className="space-y-3">
      <Callout tone="info" title={t('channelDialog.scopeTitle')}>
        {domainsElsewhere > 0 ? t('channelDialog.scopeDomains', { count: domainsElsewhere, formatted: formatCount(domainsElsewhere), name: currentName }) : t('channelDialog.scope')}
      </Callout>
      {notes.length ? (
        <Callout tone="warn" title={t('channelDialog.checks')}>
          <ul className="list-disc space-y-1 pl-4">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
    </div>
  );
}

function ChannelForm({ row, env, connections, onClose, onDone }: { row: TldChannelRow; env: IntegrationEnvironment; connections: readonly ConnectionSummary[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [connectionId, setConnectionId] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const state = row.current;
  const write = useWrite((body: ProposeChannelBody, opts) => api.proposeTldChannel(row.tld_id, body, opts));
  const target = connections.find((c) => c.id === connectionId);
  const verdicts = useVerdicts([row]);
  const gtld = row.tld_class === 'GTLD' || row.tld_class === 'NEW_GTLD';
  const domainsHeldByCurrent = row.domains_by_connection.find((d) => d.connection.id === state.connection.id)?.domains ?? 0;
  const pendingOpen = !!state.pending;
  const submit = async () => {
    const res = await write.run({ connection_id: connectionId, expected_active_version: state.source === 'CONFIGURED' ? state.version : null, reason: reason.trim() });
    if (res) onDone();
  };
  if (write.state.phase === 'done') {
    const r = write.state.result;
    return (
      <div className="space-y-4">
        <WriteOutcome result={r.result} approval={r.approval} warnings={r.warnings} env={env} appliedText={t('channelDialog.applied')} />
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <div className="grid gap-1 rounded-lg border p-3 text-sm">
        <span className="text-xs font-medium text-muted-foreground">{t('channelDialog.currentLabel')}</span>
        <span className="flex flex-wrap items-center gap-2">
          <ChannelBadge channel={state.channel} />
          <ConnectionLink connection={state.connection} />
          <span className="text-xs text-muted-foreground">{t(`sourceHint.${state.source}`)}</span>
        </span>
      </div>
      {pendingOpen ? (
        <Callout tone="pending">{t('channelDialog.pendingExists')}</Callout>
      ) : (
        <>
          <ConnectionPicker value={connectionId} onChange={setConnectionId} connections={connections} current={state.connection.id} verdicts={verdicts} zone={row.unicode_zone || row.zone} />
          {target ? (
            <p className="flex flex-wrap items-center gap-2 text-sm">
              {t('channelDialog.newChannel')} <ChannelBadge channel={channelOf(target.kind, target.protocol)} />
            </p>
          ) : null}
          <PreChecks target={target} gtld={gtld} domainsElsewhere={domainsHeldByCurrent} currentName={state.connection.name} />
          <ReasonField value={reason} onChange={setReason} />
          <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
        </>
      )}
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        {!pendingOpen ? (
          <Button type="button" onClick={() => void submit()} disabled={!connectionId || !reason.trim() || !confirmed || write.busy} variant={env === 'PRODUCTION' ? 'destructive' : 'default'}>
            <ArrowRightLeft /> {env === 'PRODUCTION' ? t('form.submitForApproval') : t('form.saveNow')}
          </Button>
        ) : null}
      </DialogFooter>
    </div>
  );
}

function BatchForm({ rows, env, connections, onClose, onDone }: { rows: readonly TldChannelRow[]; env: IntegrationEnvironment; connections: readonly ConnectionSummary[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [connectionId, setConnectionId] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ChannelBatchBody, opts) => api.proposeTldChannelBatch(body, opts));
  const zoneOf = (id: string | null) => rows.find((r) => r.tld_id === id)?.unicode_zone ?? '';
  const verdicts = useVerdicts(rows);
  const submit = async () => {
    const res = await write.run({ connection_id: connectionId, tld_ids: rows.map((r) => r.tld_id), reason: reason.trim() });
    if (res) onDone();
  };
  if (write.state.phase === 'done') {
    const r: ChannelBatchResult = write.state.result;
    return (
      <div className="space-y-4">
        <WriteOutcome result={r.result} approval={r.approval} warnings={[]} env={env} appliedText={t('channelDialog.applied')} />
        <p className="text-sm">{t('batchDialog.included', { count: r.items.length })}</p>
        {r.skipped.length ? (
          <Callout tone="neutral" title={t('batchDialog.skipped', { count: r.skipped.length })}>
            <ul className="list-disc pl-4">
              {r.skipped.map((s) => (
                <li key={s.tld_id}>
                  .{zoneOf(s.tld_id)} — {tx(`batchDialog.skip.${s.code}`, s.code)}
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}
        {r.warnings.length ? (
          <WarningList warnings={r.warnings.map((w) => ({ code: w.code, field: w.tld_id ? `.${zoneOf(w.tld_id)}` : null }))} />
        ) : null}
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <div className="grid gap-1.5">
        <span className="text-sm font-medium">{t('batchDialog.tlds')}</span>
        <ul className="flex flex-wrap gap-1.5">
          {rows.map((r) => (
            <li key={r.tld_id}>
              <Badge tone="neutral" icon={false}>
                .{r.unicode_zone}
              </Badge>
            </li>
          ))}
        </ul>
      </div>
      <ConnectionPicker value={connectionId} onChange={setConnectionId} connections={connections} verdicts={verdicts} />
      <Callout tone="info" title={t('channelDialog.scopeTitle')}>
        {t('channelDialog.scope')}
      </Callout>
      <p className="text-xs text-muted-foreground">{t('batchDialog.skipHint')}</p>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!connectionId || !rows.length || !reason.trim() || !confirmed || write.busy} variant={env === 'PRODUCTION' ? 'destructive' : 'default'}>
          <ArrowRightLeft /> {env === 'PRODUCTION' ? t('form.submitForApproval') : t('form.saveNow')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function TldHistory({ tldId, zone }: { tldId: string; zone?: string }) {
  const { t } = useTranslation('integrations');
  const result = useApi((signal) => api.getTldChannel(tldId, { signal }), [tldId]);
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const columns: Column<TldChannelVersion>[] = [
    { key: 'v', header: t('versions.version'), cell: (v) => v.version },
    { key: 's', header: t('versions.status'), cell: (v) => <StatusBadge group="versionStatus" value={v.status} /> },
    {
      key: 'c',
      header: t('tldHistory.connection'),
      cell: (v) => (
        <div className="grid gap-1">
          <ChannelBadge channel={v.channel} />
          <ConnectionLink connection={v.connection} />
        </div>
      ),
    },
    { key: 'r', header: t('versions.reason'), cell: (v) => v.reason, className: 'min-w-40' },
    {
      key: 'by',
      header: t('versions.createdBy'),
      cell: (v) => (
        <div className="grid gap-0.5 text-xs">
          <StaffName staff={v.created_by} />
          <When at={v.created_at} />
          {v.approval ? <StatusBadge group="approvalStatus" value={v.approval.status} /> : null}
        </div>
      ),
    },
  ];
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {zone ? t('tldHistory.defaultOf', { zone }) : t('tldHistory.default')}: <ConnectionLink connection={result.data.default_connection} />
      </p>
      <DataTable columns={columns} rows={result.data.history} rowKey={(v) => v.id} empty={<EmptyState title={t('tldHistory.none')}>{t('tldHistory.noneHint')}</EmptyState>} />
    </div>
  );
}
